/**
 * BuildingLabelsOverlay - Renders labels above all buildings
 *
 * Shows:
 * - Building area code (default) or name, controlled by `labelMode`
 * - In 'code' mode, hovering a label reveals the full building name
 * - Edit (gear) button - opens building in edit mode
 *
 * Labels are shown by default for all buildings in view mode.
 */

import { useEffect, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { Settings, Pencil, Copy, Plus, LayoutGrid } from 'lucide-react'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FEET_TO_METERS } from '@/constants/units'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

/** Which text the building labels display by default */
export type BuildingLabelMode = 'code' | 'name'

interface BuildingLabelsOverlayProps {
  map: mapboxgl.Map | null
  buildings: Building3DConfigExtended[]
  mapCenter: { lat: number; lng: number } | null
  /** Whether labels show the area code or the full name (default: 'code') */
  labelMode?: BuildingLabelMode
  onEditClick?: (buildingId: string) => void
  onDuplicateClick?: (buildingId: string) => void
  onAddObstacle?: (buildingId: string) => void
  onAddSubunit?: (buildingId: string) => void
}

interface BuildingScreenPosition {
  buildingId: string
  buildingName: string
  buildingCode: string
  x: number
  y: number
  visible: boolean
}

export function BuildingLabelsOverlay({
  map,
  buildings,
  mapCenter,
  labelMode = 'code',
  onEditClick,
  onDuplicateClick,
  onAddObstacle,
  onAddSubunit,
}: BuildingLabelsOverlayProps) {
  const [screenPositions, setScreenPositions] = useState<BuildingScreenPosition[]>([])

  const mode = useBuildingEditorStore((state) => state.mode)
  const editingBuildingId = useBuildingEditorStore((state) => state.editingBuildingId)

  /**
   * Convert 3D world position (feet from mapCenter) to 2D screen coordinates
   */
  const project3DToScreen = useCallback(
    (position: [number, number, number], buildingHeight: number): { x: number; y: number; visible: boolean } => {
      if (!map || !mapCenter) {
        return { x: 0, y: 0, visible: false }
      }

      try {
        // Convert position from feet to meters
        const offsetX = position[0] * FEET_TO_METERS
        const offsetZ = position[2] * FEET_TO_METERS

        // Get reference Mercator coordinate at map center
        const refMercator = mapboxgl.MercatorCoordinate.fromLngLat(
          [mapCenter.lng, mapCenter.lat],
          0
        )

        // Calculate new Mercator coordinate
        const scale = refMercator.meterInMercatorCoordinateUnits()
        const newMercator = new mapboxgl.MercatorCoordinate(
          refMercator.x + offsetX * scale,
          refMercator.y + offsetZ * scale, // Scene +Z = south aligns with Mercator +Y = south
          refMercator.z
        )

        // Convert to screen coordinates
        const lngLat = newMercator.toLngLat()
        const screenPoint = map.project(lngLat)

        // Check if within viewport
        const canvas = map.getCanvas()
        const visible =
          screenPoint.x >= -100 &&
          screenPoint.x <= canvas.width + 100 &&
          screenPoint.y >= -100 &&
          screenPoint.y <= canvas.height + 100

        // Offset above building based on height (approximate)
        const heightOffset = Math.min(80, buildingHeight * 2)

        return {
          x: screenPoint.x,
          y: screenPoint.y - heightOffset,
          visible,
        }
      } catch (error) {
        console.error('Error projecting position:', error)
        return { x: 0, y: 0, visible: false }
      }
    },
    [map, mapCenter]
  )

  /**
   * Update screen positions when camera moves or buildings change
   */
  const updatePositions = useCallback(() => {
    if (!buildings || buildings.length === 0) {
      setScreenPositions([])
      return
    }

    const positions: BuildingScreenPosition[] = buildings
      .filter(building => building.id !== editingBuildingId) // Don't show label for building being edited
      .map(building => {
        const position = building.position as [number, number, number]
        const screenPos = project3DToScreen(position, building.height)

        return {
          buildingId: building.id,
          buildingName: building.name,
          buildingCode: building.code || building.name,
          x: screenPos.x,
          y: screenPos.y,
          visible: screenPos.visible,
        }
      })
      .filter(pos => pos.visible)

    setScreenPositions(positions)
  }, [buildings, editingBuildingId, project3DToScreen])

  // Update positions on camera move
  useEffect(() => {
    if (!map) return

    const handleMove = () => updatePositions()

    map.on('move', handleMove)
    map.on('zoom', handleMove)
    map.on('pitch', handleMove)
    map.on('bearing', handleMove)

    // Initial position update
    updatePositions()

    return () => {
      map.off('move', handleMove)
      map.off('zoom', handleMove)
      map.off('pitch', handleMove)
      map.off('bearing', handleMove)
    }
  }, [map, updatePositions])

  // Update positions when buildings change
  useEffect(() => {
    updatePositions()
  }, [updatePositions])

  // Don't render labels in add/edit mode
  if (mode !== 'view') {
    return null
  }

  return (
    <>
      {screenPositions.map((pos) => {
        // In 'code' mode show the area code; in 'name' mode show the full name.
        // The full name is always available as the title tooltip on hover.
        const primaryText = labelMode === 'name' ? pos.buildingName : pos.buildingCode
        return (
        <div
          key={pos.buildingId}
          className="absolute z-20 pointer-events-auto"
          style={{
            left: pos.x,
            top: pos.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {/* Building label with edit button */}
          <div className="flex items-center gap-1 p-1 bg-background/90 rounded-lg shadow-md backdrop-blur-sm">
            {/* Building code / name */}
            <span
              className="px-2 py-0.5 text-xs font-medium whitespace-nowrap"
              title={pos.buildingName}
            >
              {primaryText}
            </span>

            {/* Actions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-6 w-6 p-0"
                  onClick={(e) => e.stopPropagation()}
                  title="Building actions"
                >
                  <Settings className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[120px]">
                <DropdownMenuItem onClick={() => onEditClick?.(pos.buildingId)}>
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  Edit
                </DropdownMenuItem>
                {onDuplicateClick && (
                  <DropdownMenuItem onClick={() => onDuplicateClick(pos.buildingId)}>
                    <Copy className="mr-2 h-3.5 w-3.5" />
                    Duplicate
                  </DropdownMenuItem>
                )}
                {onAddObstacle && (
                  <DropdownMenuItem onClick={() => onAddObstacle(pos.buildingId)}>
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    Add Obstacle
                  </DropdownMenuItem>
                )}
                {onAddSubunit && buildings.find(b => b.id === pos.buildingId)?.templateType === 'single_unit_community' && (
                  <DropdownMenuItem onClick={() => onAddSubunit(pos.buildingId)}>
                    <LayoutGrid className="mr-2 h-3.5 w-3.5" />
                    Add Subunit
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        )
      })}
    </>
  )
}
